import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { getLocations } from '../services/api';

const Locations = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await getLocations();
        setLocations(data);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white text-xl">Loading locations...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative -mt-24 md:-mt-40 pb-20 overflow-hidden z-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-6 space-y-2">
          <h2 className="text-xs md:text-sm font-black text-white tracking-wide uppercase">
            Select Your Jump Location
          </h2>
        </div>

        {/* Desktop View - Horizontal Scrollable Layout */}
        <div className="hidden lg:block relative">
          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex justify-center gap-3 overflow-x-auto scrollbar-hide scroll-smooth px-12 py-4"
            style={{
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {locations.map((location, index) => (
              <div
                key={location.id}
                onClick={() => !location.comingSoon && navigate(`/location/${location.slug || location.name.toLowerCase().replace(/\s+/g, '-')}`)}
                className="group relative flex-shrink-0 w-32 h-[200px] rounded-2xl overflow-hidden cursor-pointer transform hover:scale-105 hover:z-10 transition-all duration-500 shadow-2xl"
                style={{
                  scrollSnapAlign: 'center',
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                }}
              >
                {/* Background Image */}
                <img
                  src={location.image}
                  alt={location.name}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/42 to-transparent group-hover:from-black/90 transition-all duration-300"></div>

                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-3">
                  <div className="transform transition-all duration-300">
                    <h3 className="text-base font-black text-white mb-1 group-hover:text-yellow-400 transition-colors">
                      {location.name}
                    </h3>
                    <p className="text-gray-300 text-xs mb-1 group-hover:text-white transition-colors">
                      {location.comingSoon ? 'Park' : 'Jump Location'}
                    </p>
                    {location.comingSoon ? (
                      <div className="inline-block bg-yellow-400 text-black px-2 py-1 rounded-full text-xs font-bold">
                        Opening Soon
                      </div>
                    ) : (
                      <>
                        <p className="text-gray-400 text-xs mb-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-y-0 translate-y-2 hidden group-hover:block">
                          {location.description || 'Experience the thrill'}
                        </p>
                        <div className="flex items-center space-x-1 text-yellow-400 font-bold text-xs opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-y-0 translate-y-2">
                          <span>BOOK NOW</span>
                          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Hover Border Effect */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-yellow-400 rounded-2xl transition-all duration-300"></div>

                {/* Shine Effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                  <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent transform skew-x-12 group-hover:left-full transition-all duration-1000"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile/Tablet View - Horizontal Scrollable Carousel */}
        <div className="lg:hidden relative">
          {/* Scroll Buttons */}
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-yellow-400 hover:bg-yellow-300 text-black p-2 rounded-full shadow-lg"
            aria-label="Scroll left"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-yellow-400 hover:bg-yellow-300 text-black p-2 rounded-full shadow-lg"
            aria-label="Scroll right"
          >
            <ChevronRight size={18} />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth px-10 py-4"
            style={{
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {locations.map((location, index) => (
              <div
                key={location.id}
                onClick={() => !location.comingSoon && navigate(`/location/${location.slug || location.name.toLowerCase().replace(/\s+/g, '-')}`)}
                className="flex-shrink-0 w-32 h-[200px] rounded-2xl overflow-hidden cursor-pointer shadow-2xl"
                style={{
                  scrollSnapAlign: 'center',
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                }}
              >
                {/* Background Image */}
                <div className="relative w-full h-full">
                  <img
                    src={location.image}
                    alt={location.name}
                    className="w-full h-full object-cover"
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/42 to-transparent"></div>

                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-3">
                    <h3 className="text-base font-black text-white mb-1">
                      {location.name}
                    </h3>
                    <p className="text-gray-300 text-xs mb-1">
                      {location.comingSoon ? 'Park' : 'Jump Location'}
                    </p>
                    {location.comingSoon ? (
                      <div className="inline-block bg-yellow-400 text-black px-2 py-1 rounded-full text-xs font-bold w-fit">
                        Opening Soon
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1 text-yellow-400 font-bold text-xs">
                        <span>BOOK NOW</span>
                        <ArrowRight size={14} />
                      </div>
                    )}
                  </div>

                  {/* Border Effect */}
                  <div className="absolute inset-0 border-2 border-yellow-400/50 rounded-2xl"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Scroll Indicator Dots */}
          <div className="flex justify-center mt-6 gap-2">
            {locations.map((_, index) => (
              <div
                key={index}
                className="w-2 h-2 rounded-full bg-gray-600"
              ></div>
            ))}
          </div>
        </div>
      </div>

      {/* Custom CSS for hiding scrollbar */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default Locations;